import { useState } from 'react';
import { format } from 'date-fns';
import type { Rehearsal } from '../lib/types';
import { RUMINATION_LABELS, OUTCOME_LABELS } from '../lib/types';
import { deleteRehearsal } from '../lib/store';

interface Props {
  rehearsals: Rehearsal[];
  onRefresh: () => void;
}

export function HistoryList({ rehearsals, onRefresh }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);

  if (rehearsals.length === 0) {
    return <div className="text-[var(--text-muted)] text-sm py-8">No rehearsals saved yet. Your history and curve will populate after the first one.</div>;
  }

  return (
    <div className="space-y-2">
      {rehearsals.map((r) => {
        const open = openId === r.id;
        const themeLabel = (RUMINATION_LABELS as any)[r.rumination_theme] || r.rumination_theme;
        const delta = r.tolerance_after - r.tolerance_before;

        return (
          <div key={r.id} className="rounded-2xl border border-[var(--border)] overflow-hidden">
            <button
              onClick={() => setOpenId(open ? null : r.id)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--bg-elev)]"
            >
              <div>
                <div className="text-sm">{format(new Date(r.created_at), 'PPP')}</div>
                <div className="text-[var(--text-muted)] text-sm mt-0.5 line-clamp-1">{themeLabel}</div>
              </div>
              <div className="text-right tabular-nums">
                <div className="text-lg font-medium tracking-tighter">{r.tolerance_after}</div>
                <div className="text-[10px] text-[var(--text-subtle)] -mt-1">after • {delta >= 0 ? '+' : ''}{delta}</div>
              </div>
            </button>

            {open && (
              <div className="px-5 pb-6 pt-1 text-sm border-t border-[var(--border)] bg-[var(--bg-elev)]/60 space-y-4">
                <div>
                  <div className="text-[var(--text-subtle)] text-xs mb-1">GRATITUDE (START)</div>
                  <div className="leading-snug">{r.gratitude}</div>
                </div>
                {r.scenario && (
                  <div>
                    <div className="text-[var(--text-subtle)] text-xs mb-1">SCENARIO / COST</div>
                    <div className="leading-snug">{r.scenario}</div>
                  </div>
                )}
                <div>
                  <div className="text-[var(--text-subtle)] text-xs mb-1">THE MOVE</div>
                  <div><span className="font-medium">{OUTCOME_LABELS[r.outcome_type]}:</span> {r.outcome_text}</div>
                </div>

                <div className="flex gap-6 text-xs pt-2 border-t border-[var(--border)]">
                  <div>Reappraisal success: <span className="tabular-nums font-medium">{r.reappraisal_success}</span>/10</div>
                  <div>Residue drop (avg): <span className="tabular-nums font-medium">{((Object.values(r.emotional_residue_before).reduce((a, b) => a + b, 0) - Object.values(r.emotional_residue_after).reduce((a, b) => a + b, 0)) / 4).toFixed(1)}</span></div>
                  <div>Loop frequency: <span className="font-medium">{r.repeat_loop_frequency}</span></div>
                </div>

                <div className="pt-2 flex gap-3">
                  <button
                    onClick={() => {
                      if (confirm('Delete this rehearsal permanently? This cannot be undone.')) {
                        deleteRehearsal(r.id);
                        onRefresh();
                        setOpenId(null);
                      }
                    }}
                    className="text-xs text-red-600/70 hover:text-red-600"
                  >
                    Delete this entry
                  </button>
                  <button onClick={() => setOpenId(null)} className="text-xs text-[var(--text-muted)]">Close</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
